<template>
  <div>
    <p
      v-if="error"
      class="text-danger"
      >{{ errorMsg }}</p
    >

    <b-card class="shadow-sm">
      <template #header>
        <div class="d-flex justify-content-between align-items-center">
          <h5 class="mb-0">
            {{ $t('vendorLocationList.title') }}
            <b-badge
              v-if="locations.length"
              variant="soft-info"
              class="ml-2"
              >{{ locations.length }}</b-badge
            >
          </h5>
          <b-button
            variant="sharp-primary"
            size="sm"
            @click="addLocation"
            :disabled="isDisabled"
          >
            <b-icon
              icon="plus-circle"
              class="mr-2"
            ></b-icon>
            {{ $t('vendorLocationList.addLocation') }}
          </b-button>
        </div>
      </template>

      <div>
        <!-- Location List Area -->

        <busy-spinner
          v-if="loading && isFirstLoad"
          key="loadingList"
          inner-class="my-5"
        ></busy-spinner>

        <div
          v-else-if="!loading && locations && locations.length"
          key="list"
        >
          <div
            v-for="location in locations"
            :key="location.id"
            class="mb-4"
          >
            <company-location-card
              :location="location"
              :hide-map="hideMap"
              :disabled="isDisabled"
              @request-edit="handleLocationEdit"
              @deleted="handleDeleted"
              @request-set-primary="setPrimaryLocation"
            ></company-location-card>
          </div>
        </div>

        <div
          v-else-if="!loading && !locations.length"
          key="emptyList"
          class="py-5 text-center"
        >
          <h6>{{ $t('vendorLocationList.noLocations') }}</h6>
          <div class="text-center">
            <b-button
              variant="sharp-primary"
              size="sm"
              @click="addLocation"
              :disabled="isDisabled"
            >
              <b-icon
                icon="plus-circle"
                class="mr-2"
              ></b-icon>
              {{ $t('vendorLocationList.addLocation') }}
            </b-button>
          </div>
        </div>
      </div>
    </b-card>

    <company-location-modal
      ref="locationModal"
      :company-id="companyId"
      @created="reloadList"
      @updated="reloadList"
    ></company-location-modal>
  </div>
</template>

<script>
  import { suppliosApi } from '@/js/api-service';
  import CompanyLocationModal from '@/components/CompanyLocationModal';
  import CompanyLocationCard from '@/components/CompanyLocationCard';
  import orderBy from 'lodash/orderBy';

  export default {
    name: 'VendorLocationList',
    components: {
      CompanyLocationModal,
      CompanyLocationCard,
    },
    mixins: [],
    i18n: {
      messages: {
        en: {
          vendorLocationList: {
            title: 'Locations',
            addLocation: 'Add Location',
            noLocations: 'No Locations',
            errorGettingLocationList: 'Error while getting location list',
            errorUpdatingPrimaryLocation: 'Error while updating primary location',
          },
        },
        es: {
          vendorLocationList: {
            title: 'Ubicaciones',
            addLocation: 'Agregar Ubicación',
            noLocations: 'Sin ubicaciones',
            errorGettingLocationList: 'Error al obtener la lista de ubicaciones',
            errorUpdatingPrimaryLocation: 'Error al actualizar la ubicación principal',
          },
        },
        pt: {
          vendorLocationList: {
            title: 'Localizações',
            addLocation: 'Adicionar Localização',
            noLocations: 'Sem localizações',
            errorGettingLocationList: 'Erro ao obter a lista de localizações',
            errorUpdatingPrimaryLocation: 'Erro ao atualizar a localização principal',
          },
        },
        'zh-Hans': {
          vendorLocationList: {
            title: '地点',
            addLocation: '添加地点',
            noLocations: '暂无地点',
            errorGettingLocationList: '获取地点列表时出错',
            errorUpdatingPrimaryLocation: '更新主要地点时出错',
          },
        },
        'zh-Hant': {
          vendorLocationList: {
            title: '地點',
            addLocation: '新增地點',
            noLocations: '尚無地點',
            errorGettingLocationList: '取得地點清單時發生錯誤',
            errorUpdatingPrimaryLocation: '更新主要地點時發生錯誤',
          },
        },
      },
    },
    props: {
      companyId: {
        type: [String, Number],
        required: true,
      },
      hideMap: {
        type: Boolean,
        default: false,
        // set default to TRUE until Stadia maps is working!
      },
      disabled: {
        type: Boolean,
        default: false,
      },
    },
    data: function () {
      return {
        errorMsg: 'Error',
        error: false,
        isFirstLoad: true, // set false after doing it first time... so only shows spinner on initial load
        loading: false, // main loading flag
        locations: [], // list we will get from API
      };
    },
    computed: {
      isDisabled: function () {
        return this.disabled || this.loading;
      },
    },
    mounted: function () {
      this.getLocations();
    },
    methods: {
      handleError: function (error, message) {
        console.log(message);
        console.log(error);
        this.errorMsg = message;
        this.error = true;
      },
      addLocation: function () {
        this.$refs.locationModal.startCreate();
      },
      setLocationList: function (locationList) {
        // set this.locations with data from API, doing any transforms necessary
        // we have to nest anything that the vue-formulate form expects to have nested
        // for now, we won't nest phone number fields... but if you do, handle it here
        // PUT PRIMARY LOCATION FIRST - look through list, pull out any with is_primary_location = true and put first
        // use lodash orderBy for this -- it returns NEW array and is super nice
        this.locations = orderBy(locationList, ['is_primary_location', 'created'], ['desc', 'asc']); // returns a NEW array
      },
      getLocations: function () {
        this.loading = true;
        return suppliosApi.vendorStandard
          .listLocations(this.companyId)
          .then((response) => {
            this.setLocationList(response.data);
            return true;
          })
          .catch((error) => {
            this.handleError(error, this.$t('vendorLocationList.errorGettingLocationList'));
            return false;
          })
          .then((status) => {
            this.loading = false;
            this.isFirstLoad = false;
            return status;
          });
      },
      reloadList: function () {
        this.getLocations();
        // emit something to parent to refresh the company header also
        this.$emit('refresh');
      },
      handleLocationEdit: function (locationObj) {
        //let locationId = locationObj.id;
        this.$refs.locationModal.startUpdate(locationObj);
      },
      handleDeleted: function (deletedObj) {
        console.log(deletedObj);
        this.reloadList();
      },
      setPrimaryLocation: function (locationObj) {
        // this gets called from child component via event -- entire location object is passed in
        this.loading = true;
        let updateData = {};
        updateData['_primary_location'] = locationObj.id;
        return suppliosApi.vendorStandard
          .setPrimaryLocation(this.companyId, updateData)
          .then((response) => {
            // response here is the update list of locations... NOT the location we just updated
            this.setLocationList(response.data);
            this.$emit('refresh');
            return true;
          })
          .catch((error) => {
            this.handleError(error, this.$t('vendorLocationList.errorUpdatingPrimaryLocation'));
            return false;
          })
          .then((status) => {
            this.loading = false;
            return status;
          });
      },
    },
  };
</script>

<style></style>
